import { buildingFullAddressLine, buildingStreetLine } from "../utilities/buildingFlatAddress";
import { entityHref, navigableEntityId, projectHref } from "./entityNavigation";

/** Die Ebenen, die eine Entitätsseite über sich zeigt — von außen nach innen. */
export const TRAIL_TYPES = ["Project", "Building", "Floor", "Space", "Asset"] as const;
export type TrailType = (typeof TRAIL_TYPES)[number];

export interface TrailItem {
  type: TrailType;
  label: string;
  /** Fehlt bei der aktuellen Seite und bei einem Glied ohne Id. */
  href?: string;
}

/** Was über einer Seite gefunden wurde; jedes Glied darf fehlen. */
export interface TrailParts {
  project?: any;
  building?: any;
  floor?: any;
  space?: any;
  asset?: any;
}

const PART_OF: Record<TrailType, keyof TrailParts> = {
  Project: "project",
  Building: "building",
  Floor: "floor",
  Space: "space",
  Asset: "asset",
};

function labelOf(type: TrailType, entity: any, typeLabel: (type: TrailType) => string): string {
  const text = (() => {
    switch (type) {
      case "Project":
        return entity?.name;
      case "Building":
        return buildingStreetLine(entity) || buildingFullAddressLine(entity);
      case "Floor":
        return entity?.name || (entity?.level != null ? `${typeLabel(type)} ${entity.level}` : "");
      case "Space":
        return entity?.name || entity?.longName;
      case "Asset":
        return entity?.name || entity?.id;
    }
    return "";
  })();
  return (typeof text === "string" && text.trim()) || typeLabel(type);
}

/**
 * Der Pfad über einer Entitätsseite: Projekt › Gebäude › Etage › Raum.
 *
 * Nur Glieder, die gefunden wurden, kommen hinein — ein Glied ohne Namen heißt nach seiner Art,
 * eines ohne Id bleibt ohne Link (nie `selectedEntity=undefined`). Das letzte Glied ist die Seite
 * selbst und verlinkt nicht.
 */
export function buildEntityTrail(
  parts: TrailParts,
  currentType: TrailType,
  typeLabel: (type: TrailType) => string,
): TrailItem[] {
  const last = TRAIL_TYPES.indexOf(currentType);
  const items: TrailItem[] = [];

  TRAIL_TYPES.slice(0, last + 1).forEach((type, index) => {
    const entity = parts[PART_OF[type]];
    if (!entity) return;
    const isCurrent = index === last;
    const href = isCurrent
      ? undefined
      : type === "Project"
        ? projectHref(entity)
        : entityHref(type, entity);
    items.push({ type, label: labelOf(type, entity, typeLabel), href });
  });

  return items;
}

/** Das Glied, zu dem „Zurück" führt: das nächste darüber, das eine Adresse hat. */
export function parentOf(trail: TrailItem[]): TrailItem | undefined {
  return [...trail.slice(0, -1)].reverse().find((item) => !!item.href);
}

/** Die Id des Gebäudes, der Etage und des Raums, auf die eine Entität selbst zeigt. */
export function directParentIds(type: TrailType, entity: any): {
  building?: string;
  floor?: string;
  space?: string;
} {
  switch (type) {
    case "Building":
      return { building: navigableEntityId(entity) };
    case "Floor":
      return { building: navigableEntityId(entity?.building) };
    case "Space":
      return { floor: navigableEntityId(entity?.floor) };
    case "Asset":
      return {
        building: navigableEntityId(entity?.building),
        floor: navigableEntityId(entity?.floor),
        space: navigableEntityId(entity?.space),
      };
    default:
      return {};
  }
}

/** Die Liegenschaften, die das Gebäude führen — gleich, in welcher Form die Id gespeichert ist. */
export function siteIdsNamingBuilding(sites: any[], buildingId: string): string[] {
  return sites
    .filter((site) =>
      (Array.isArray(site?.buildings) ? site.buildings : []).some(
        (entry: unknown) => navigableEntityId(entry) === buildingId,
      ),
    )
    .map((site) => navigableEntityId(site?._id))
    .filter((id): id is string => !!id);
}
